import {FC, useEffect, useMemo} from 'react';
import {usePersona} from 'src/permissions/hooks';
import {employeeApi} from 'src/api';
import {AMPAutocomplete} from 'src/components/common/AMPAutocomplete';
import {FormikProps, useFormikContext} from 'formik';
import {AMPAutocompleteProps} from 'src/components/common/AMPAutocomplete/AMPAutocomplete.types';
import {getHelperText} from 'src/utils/forms';
import {useIntl} from 'react-intl';
import {CreateRfidFormFromType} from '..';
import {useBusinessId, useAppSelector} from 'src/app/hooks';

export const EmployeeForRfidDropdown: FC = () => {
    const intl = useIntl();
    const persona = usePersona();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const isBusinessUser = persona.isBackOffice;
    const {setFieldValue, handleBlur, touched, errors, values} =
        useFormikContext<CreateRfidFormFromType>();

    // The business being worked in, for everyone. It used to be sent only by a back-office user
    // with a business of their own, which left the platform administrator listing every tenant's
    // employees in a dropdown about one company.
    const employeeRequestBody = () => ({businessId});

    const {data: employeeData, refetch: employeeRefetch} = employeeApi.useSearchEmployeesQuery(
        {
            params: {
                page: 0,
                size: 1_000,
            },
            data: {
                ...employeeRequestBody(),
                companyId: values?.companyId ? Number(values?.companyId) : undefined,
            },
        },
        {
            skip: !values.companyId,
        }
    );

    const options = useMemo(() => {
        return (
            employeeData?.content?.map((employee) => {
                const label =
                    (employee?.firstName + ' ' + employee?.lastName)?.length > 0
                        ? `${employee?.firstName} ${employee?.lastName}`
                        : '';

                return {
                    id: Number(employee?.id) ?? 0,
                    label,
                };
            }) ?? []
        );
    }, [employeeData?.content]);

    const handleChangeCompanyValue = (
        setFieldValue: FormikProps<CreateRfidFormFromType>['setFieldValue'],
        fieldName: string
    ): AMPAutocompleteProps['onChange'] => {
        return (_event, newValue) => {
            // Allow clearing the field by setting to null when newValue is null/undefined
            setFieldValue(fieldName, newValue ? newValue.id : null);
        };
    };

    useEffect(() => {
        employeeRefetch();
    }, []);

    return (
        <AMPAutocomplete
            disabled={!values.companyId}
            options={options}
            value={options.find((item) => item.id === Number(values['employeeId']))}
            label={'employee'}
            input={'employeeId'}
            showAvatar
            onChange={handleChangeCompanyValue(setFieldValue, 'employeeId')}
            onBlur={handleBlur}
            hasError={
                (touched?.['employeeId'] && Boolean(errors['employeeId'])) ||
                (touched?.['employeeId'] && values['employeeId'] === 0)
            }
            helperText={getHelperText('employeeId', touched, errors, intl)}
        />
    );
};
